import { PRODUCT_NAME } from './brand';
import { formatZonedDayBoundary } from '../utils/timeZone';

export const TRANSACTION_TYPE_FILTERS = {
  ALL: 'all',
  INCOME: 'income',
  EXPENSE: 'expense',
};

export const HISTORY_FILTER_FIELDS = Object.freeze({
  CARDS: 'card_id',
  DATES: 'original_utc_datetime',
  TYPE: 'type',
});

export const HISTORY_OPERATION_TYPE_BY_FILTER = Object.freeze({
  [TRANSACTION_TYPE_FILTERS.ALL]: null,
  [TRANSACTION_TYPE_FILTERS.INCOME]: 'income',
  [TRANSACTION_TYPE_FILTERS.EXPENSE]: 'outcome',
});

function normalizeHistoryDateKey(value) {
  const match = String(value ?? '').trim().match(/^(\d{4}-\d{2}-\d{2})/);

  return match?.[1] || '';
}

export function getHistoryDateOperationFilters(dateRange, timeZone) {
  const start = normalizeHistoryDateKey(dateRange?.start ?? dateRange?.min);
  const end = normalizeHistoryDateKey(dateRange?.end ?? dateRange?.max);

  if (!start || !end) {
    return undefined;
  }

  const min = formatZonedDayBoundary(start, 'start', timeZone);
  const max = formatZonedDayBoundary(end, 'end', timeZone);

  if (!min || !max) {
    return undefined;
  }

  return [{
    field: HISTORY_FILTER_FIELDS.DATES,
    type: 'date_range',
    value: {
      min,
      max,
    },
  }];
}

export function getHistoryOperationFilters(type, {
  cardIds = [],
  dateRange = null,
  timeZone,
} = {}) {
  const operationType = HISTORY_OPERATION_TYPE_BY_FILTER[type];
  const filters = [];

  if (operationType) {
    filters.push({
      field: HISTORY_FILTER_FIELDS.TYPE,
      type: 'enum',
      value: [operationType],
    });
  }

  filters.push(
    ...(getHistoryCardOperationFilters(cardIds) || []),
    ...(getHistoryDateOperationFilters(dateRange, timeZone) || [])
  );

  return filters.length > 0 ? filters : undefined;
}

export function getHistoryCardOperationFilters(cardIds) {
  const normalizedCardIds = Array.from(new Set(
    (Array.isArray(cardIds) ? cardIds : [cardIds])
      .map((cardId) => String(cardId ?? '').trim())
      .filter(Boolean)
  ));

  if (normalizedCardIds.length === 0) {
    return undefined;
  }

  return [{
    field: HISTORY_FILTER_FIELDS.CARDS,
    type: 'card_enum',
    value: normalizedCardIds,
  }];
}

export const TRANSACTIONS = [
  {
    id: 'apple',
    type: 'expense',
    icon: 'A',
    iconVariant: 'dark',
    merchant: 'APL*APPLE ONLINE STORE 0089',
    createdAt: '2026-06-17T10:50:00',
    dateGroup: 'today',
    amount: -1599,
    amountFormatted: '- $1,599.00',
    cardId: 'main',
    cardLabel: `${PRODUCT_NAME} ·· 8866`,
    category: 'Shopping',
    reference: 'TXN-90231',
    status: 'completed',
  },
  {
    id: 'uber',
    type: 'expense',
    icon: 'U',
    iconVariant: 'dark',
    merchant: 'UBER* EATS AMSTERDAM NL',
    createdAt: '2026-06-17T12:46:00',
    dateGroup: 'today',
    amount: -6.24,
    amountFormatted: '- $6.24',
    cardId: 'main',
    cardLabel: `${PRODUCT_NAME} ·· 8866`,
    category: 'Food & drink',
    reference: 'TXN-90218',
    status: 'completed',
  },
  {
    id: 'deposit',
    type: 'income',
    icon: '+',
    iconVariant: 'deposit',
    merchant: 'Top up USDT',
    subtitle: 'TRC-20',
    createdAt: '2026-06-16T18:20:00',
    dateGroup: 'yesterday',
    amount: 500,
    amountFormatted: '+ $500.00',
    cardId: 'main',
    cardLabel: `${PRODUCT_NAME} ·· 8866`,
    category: 'Top up',
    txHash: '5e7b9d5f1c8a4a0b2d3e6f8a9c1b4d7e9f0a2b3c4d5e6f708192a3b4c5d6e7f8',
    blockscannerUrl:
      'https://tronscan.org/#/transaction/5e7b9d5f1c8a4a0b2d3e6f8a9c1b4d7e9f0a2b3c4d5e6f708192a3b4c5d6e7f8',
    status: 'completed',
  },
  {
    id: 'spotify',
    type: 'expense',
    icon: 'S',
    iconVariant: 'spotify',
    merchant: 'SPOTIFY P0A1B2 STOCKHOLM',
    createdAt: '2026-06-16T13:09:00',
    dateGroup: 'yesterday',
    amount: -9.9,
    amountFormatted: '- $9.90',
    cardId: 'main',
    cardLabel: `${PRODUCT_NAME} ·· 8866`,
    category: 'Entertainment',
    reference: 'TXN-90102',
    status: 'completed',
  },
  {
    id: 'starbucks',
    type: 'expense',
    icon: 'G',
    iconVariant: 'muted',
    merchant: 'TST* STARBUCKS 1234 LONDON',
    createdAt: '2026-06-16T09:15:00',
    dateGroup: 'yesterday',
    amount: -42.1,
    amountFormatted: '- $42.10',
    cardId: 'travel',
    cardLabel: `${PRODUCT_NAME} ·· 2245`,
    category: 'Food & drink',
    reference: 'TXN-90088',
    status: 'completed',
  },
];

export const DATE_GROUP_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
};

export const DEFAULT_HISTORY_FILTERS = {
  type: TRANSACTION_TYPE_FILTERS.ALL,
  dateRange: null,
  cardIds: [],
  cardId: 'all',
  status: 'all',
  sortBy: 'newest',
  query: '',
};
